/**
 * Browser-side OpenTelemetry setup for AdaptCX portals + widget.
 *
 * Mirrors the server-side `install_apm()` in `adaptcx_core.observability.apm`.
 * Call once per bundle at startup (main.ts) before any spans need to be
 * emitted.
 *
 * Design:
 * - Traces only. Browser metrics via OTel are still nascent and not worth
 *   the bundle cost yet.
 * - Auto-instrumentation for fetch + XMLHttpRequest — every outbound HTTP
 *   call becomes a span with `traceparent` header set, so a click that
 *   fires an API call links straight through to the backend trace.
 * - OTLP/HTTP exporter targeting the api-gateway's OTLP proxy at
 *   `/api/v1/otlp/v1/traces`. api-gateway validates the JWT + forwards to
 *   the in-cluster Collector. This keeps the browser using its existing
 *   auth cookie/token, no separate credential needed.
 * - Safe no-op when OTel setup fails — swallows errors and returns.
 */

// ─── Types ───────────────────────────────────────────────────────────────
export interface BrowserApmConfig {
  /** Service name stamped on every span (e.g., 'widget', 'agent-desktop'). */
  serviceName: string
  /** Full URL of the OTLP HTTP endpoint. Typically the api-gateway proxy at
   *  `${apiBase}/api/v1/otlp/v1/traces`. */
  otlpEndpoint: string
  /** Optional tenant ID stamped on every span for per-tenant filtering. */
  tenantId?: string
  /** Optional environment label (dev / staging / prod). */
  environment?: string
  /** Extra headers on every OTLP request. Typically Authorization: Bearer
   *  <jwt>. */
  headers?: Record<string, string>
  /** Domains to which the fetch/XHR instrumentors will propagate
   *  `traceparent`. Defaults to same-origin only; add the api-gateway
   *  domain here to enable cross-origin propagation. */
  propagateTraceHeaderUrls?: (string | RegExp)[]
}

let _installed = false

/**
 * Set up OpenTelemetry in the browser. Idempotent — calling twice does
 * nothing.
 */
export async function installBrowserApm(config: BrowserApmConfig): Promise<void> {
  if (_installed) return
  if (!config.otlpEndpoint) {
    // No endpoint means no export path — bail. Not an error; just a signal
    // the portal doesn't have OTel enabled in this environment.
    return
  }

  try {
    const [
      { WebTracerProvider },
      { BatchSpanProcessor },
      { OTLPTraceExporter },
      { ZoneContextManager },
      { registerInstrumentations },
      { FetchInstrumentation },
      { XMLHttpRequestInstrumentation },
      { Resource },
    ] = await Promise.all([
      import('@opentelemetry/sdk-trace-web'),
      import('@opentelemetry/sdk-trace-base'),
      import('@opentelemetry/exporter-trace-otlp-http'),
      import('@opentelemetry/context-zone'),
      import('@opentelemetry/instrumentation'),
      import('@opentelemetry/instrumentation-fetch'),
      import('@opentelemetry/instrumentation-xml-http-request'),
      import('@opentelemetry/resources'),
    ])

    const attributes: Record<string, string> = {
      'service.name': config.serviceName,
    }
    if (config.environment) {
      attributes['deployment.environment'] = config.environment
    }
    if (config.tenantId) {
      attributes['adaptcx.tenant_id'] = config.tenantId
    }
    const resource = new Resource(attributes)

    const provider = new WebTracerProvider({ resource })

    const exporter = new OTLPTraceExporter({
      url: config.otlpEndpoint,
      headers: config.headers ?? {},
    })
    provider.addSpanProcessor(new BatchSpanProcessor(exporter))

    provider.register({
      contextManager: new ZoneContextManager(),
    })

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls:
            config.propagateTraceHeaderUrls ?? [/.*/],
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls:
            config.propagateTraceHeaderUrls ?? [/.*/],
        }),
      ],
    })

    _installed = true
    // eslint-disable-next-line no-console
    console.log(
      `[APM] Browser OTel installed → ${config.otlpEndpoint} (service=${config.serviceName})`
    )
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[APM] Browser OTel setup failed — tracing disabled:', err)
  }
}

// ─── WebSocket trace-context helpers ────────────────────────────────────
// Server-side counterparts live at
// `adaptcx_core.observability.spans.inject_ws_trace_context` /
// `extract_ws_trace_context`. Same wire format (W3C traceparent).

/**
 * Return a dict of the current OTel trace context in W3C traceparent form.
 * Merge into a WebSocket message payload BEFORE sending:
 *
 *     socket.send(JSON.stringify({
 *       type: 'user_message',
 *       payload: { ... },
 *       _trace_context: injectTraceContext(),
 *     }))
 *
 * Returns an empty object when OTel isn't loaded or no span is active —
 * safe to always merge into every outbound message.
 */
export async function injectTraceContext(): Promise<Record<string, string>> {
  try {
    const { propagation, context } = await import('@opentelemetry/api')
    const carrier: Record<string, string> = {}
    propagation.inject(context.active(), carrier)
    return carrier
  } catch {
    return {}
  }
}

/**
 * Emit a manual browser-side span for a discrete user action (button
 * click, form submit, message send). Downstream server work triggered
 * during the span attaches as a child if the traceparent propagates.
 *
 *     await withUserActionSpan('send_message', { conversation_id }, async () => {
 *       await sendToWebSocket(...)
 *     })
 */
export async function withUserActionSpan<T>(
  name: string,
  attributes: Record<string, string | number | boolean>,
  fn: () => Promise<T>
): Promise<T> {
  let tracer
  try {
    const { trace } = await import('@opentelemetry/api')
    tracer = trace.getTracer('adaptcx-browser')
  } catch {
    return fn()
  }
  if (!tracer) return fn()

  const span = tracer.startSpan(name, { attributes })
  try {
    const result = await fn()
    span.end()
    return result
  } catch (err) {
    try {
      if (err instanceof Error) {
        span.recordException(err)
      }
    } catch {
      // ignore
    }
    span.end()
    throw err
  }
}
